// src/components/paginas/InventarioHistorial.js
import React, { useEffect, useMemo, useState } from 'react';

const resolveApiBase = () => {
  const env = (process.env.REACT_APP_API_BASE || '').trim();
  if (env) return env.replace(/\/+$/, '');
  const isLocal = ['localhost','127.0.0.1'].includes(window.location.hostname);
  return (isLocal ? 'http://localhost:5001' : window.location.origin).replace(/\/+$/, '');
};
const API_BASE = resolveApiBase();
const api = (p) => `${API_BASE}${p.startsWith('/') ? p : `/${p}`}`;

function usePagerFetcher(urlBase, watch = '') {
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [estado, setEstado] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const url = useMemo(() => {
    const sp = new URLSearchParams();
    if (q) sp.set('q', q);
    if (estado) sp.set('estado', estado);
    if (from) sp.set('from', from);
    if (to) sp.set('to', to);
    sp.set('page', String(page));
    sp.set('pageSize', String(pageSize));
    return `${urlBase}?${sp.toString()}`;
  }, [urlBase, q, from, to, estado, page, pageSize]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    (async () => {
      try {
        const r = await fetch(url, { credentials: 'include' });
        const j = await r.json();
        if (!r.ok || j.ok === false) throw new Error(j?.msg || `HTTP ${r.status}`);
        if (!alive) return;
        setData(j.data || []);
        setTotal(j.total || 0);
      } catch (e) {
        if (!alive) return;
        setErr(e.message || 'Error cargando');
        setData([]);
        setTotal(0);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [url]);

  return {
    state: { q, from, to, estado, page, pageSize, loading, err, data, total },
    actions: { setQ, setFrom, setTo, setEstado, setPage, setPageSize },
  };
}

export default function InventarioHistorial() {
  const [tab, setTab] = useState('cupones');

  const cupones = usePagerFetcher(api('/api/admin/cupones/historial'), 'cupones');
  const ofertas = usePagerFetcher(api('/api/ofertas/historial'), 'ofertas');

  const active = tab === 'cupones' ? cupones : ofertas;
  const { state, actions } = active;

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Historial de cupones y ofertas</h1>
      </div>

      <div className="flex gap-2 mb-4">
        <button
          className={`px-3 py-1.5 rounded-lg border ${tab==='cupones' ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-sky-700 border-sky-200'}`}
          onClick={() => setTab('cupones')}
        >Cupones</button>
        <button
          className={`px-3 py-1.5 rounded-lg border ${tab==='ofertas' ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-sky-700 border-sky-200'}`}
          onClick={() => setTab('ofertas')}
        >Ofertas</button>
      </div>

      <Filtros state={state} actions={actions} showEstado={tab==='cupones'} />

      {tab==='cupones' ? (
        <TablaCupones {...state} />
      ) : (
        <TablaOfertas {...state} />
      )}
    </div>
  );
}

function Filtros({ state, actions, showEstado }) {
  const { q, from, to, estado, pageSize, loading } = state;
  const { setQ, setFrom, setTo, setEstado, setPageSize } = actions;
  return (
    <div className="grid lg:grid-cols-5 gap-3 items-end mb-4">
      <div>
        <label className="text-xs text-slate-600">Buscar</label>
        <input value={q} onChange={e=> setQ(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2" placeholder="Nombre, producto…" />
      </div>
      <div>
        <label className="text-xs text-slate-600">Desde</label>
        <input type="date" value={from} onChange={e=> setFrom(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2" />
      </div>
      <div>
        <label className="text-xs text-slate-600">Hasta</label>
        <input type="date" value={to} onChange={e=> setTo(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2" />
      </div>
      {showEstado ? (
        <div>
          <label className="text-xs text-slate-600">Estado</label>
          <select value={estado} onChange={e=> setEstado(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2">
            <option value="">Todos</option>
            <option value="activo">Activo</option>
            <option value="inactivo">Inactivo</option>
          </select>
        </div>
      ) : <div />}
      <div>
        <label className="text-xs text-slate-600">Tamaño página</label>
        <select value={pageSize} onChange={e=> setPageSize(Number(e.target.value))} className="mt-1 w-full border rounded-lg px-3 py-2" disabled={loading}>
          {[10,20,50,100].map(n=> <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  );
}

function Paginator({ page, pageSize, total, onPage }) {
  const totalPages = Math.max(1, Math.ceil((total||0)/ (pageSize||20)));
  return (
    <div className="flex items-center justify-between mt-3 text-sm">
      <div className="text-slate-600">Total: <b>{total}</b> · Página {page} de {totalPages}</div>
      <div className="flex gap-2">
        <button className="px-3 py-1.5 rounded-lg border" disabled={page<=1} onClick={()=> onPage(page-1)}>Anterior</button>
        <button className="px-3 py-1.5 rounded-lg border" disabled={page>=totalPages} onClick={()=> onPage(page+1)}>Siguiente</button>
      </div>
    </div>
  );
}

function TablaCupones({ data, loading, err, page, pageSize, total }) {
  return (
    <div className="rounded-2xl border bg-white shadow-sm p-4">
      {loading ? (
        <div>Cargando…</div>
      ) : err ? (
        <div className="text-red-700 bg-red-50 border border-red-200 rounded p-3">{err}</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-4">Cupón</th>
                  <th className="py-2 pr-4">Producto</th>
                  <th className="py-2 pr-4">Descuento</th>
                  <th className="py-2 pr-4">Inicio</th>
                  <th className="py-2 pr-4">Fin</th>
                  <th className="py-2 pr-0 text-right">Estado</th>
                </tr>
              </thead>
              <tbody>
                {(data||[]).map((c) => (
                  <tr key={c.id} className="border-b last:border-0">
                    <td className="py-2 pr-4">
                      <div className="font-medium">{c.nombre}</div>
                      {!!c.descripcion && <div className="text-xs text-slate-500">{c.descripcion}</div>}
                    </td>
                    <td className="py-2 pr-4">{c.producto_nombre_snapshot || '—'}</td>
                    <td className="py-2 pr-4">-{Number(c.porcentaje_descuento||0)}%</td>
                    <td className="py-2 pr-4">{c.fecha_inicio ? new Date(c.fecha_inicio).toLocaleString() : '—'}</td>
                    <td className="py-2 pr-4">{c.fecha_fin ? new Date(c.fecha_fin).toLocaleString() : '—'}</td>
                    <td className="py-2 pr-0 text-right">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-slate-100">{c.estado_temporal || c.estado || '—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Paginator page={page} pageSize={pageSize} total={total} onPage={() => {}} />
        </>
      )}
    </div>
  );
}

function TablaOfertas({ data, loading, err, page, pageSize, total }) {
  const Q = (n) => `Q${(Number(n)||0).toFixed(2)}`;
  return (
    <div className="rounded-2xl border bg-white shadow-sm p-4">
      {loading ? (
        <div>Cargando…</div>
      ) : err ? (
        <div className="text-red-700 bg-red-50 border border-red-200 rounded p-3">{err}</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-4">Producto</th>
                  <th className="py-2 pr-4">Tipo</th>
                  <th className="py-2 pr-4">Descripción</th>
                  <th className="py-2 pr-4">Precio oferta</th>
                  <th className="py-2 pr-4">Inicio</th>
                  <th className="py-2 pr-4">Fin</th>
                  <th className="py-2 pr-0 text-right">Estado</th>
                </tr>
              </thead>
              <tbody>
                {(data||[]).map((o) => (
                  <tr key={o.id} className="border-b last:border-0">
                    <td className="py-2 pr-4">{o.nombreProducto}</td>
                    <td className="py-2 pr-4">{o.tipoOferta}</td>
                    <td className="py-2 pr-4">{o.descripcion || '—'}</td>
                    <td className="py-2 pr-4">{Q(o.precioOferta)}</td>
                    <td className="py-2 pr-4">{o.fechaInicio ? new Date(o.fechaInicio).toLocaleString() : '—'}</td>
                    <td className="py-2 pr-4">{o.fechaFin ? new Date(o.fechaFin).toLocaleString() : '—'}</td>
                    <td className="py-2 pr-0 text-right">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-slate-100">{o.estado_temporal || '—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Paginator page={page} pageSize={pageSize} total={total} onPage={() => {}} />
        </>
      )}
    </div>
  );
}
